import { MutationFunctionContext, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../core/i18n/hooks/use-language';
import { BASE_BACKEND_URL } from '../backend/constants';
import { useAuthenticatedMutation } from '../backend/hooks/use-authenticated-mutation';
import {
  Container,
  PlaceOrderRequestBody,
  PlaceOrderResponse,
  PutArticleInCartRequestBody,
  PutArticleInCartResponse,
} from '../backend/openapi/api-types';
import {
  buildMaxConfigNotification,
  buildTextAndIconNotification,
} from '../notifications/helpers/notification-factory';
import { useNotification } from '../notifications/hooks/use-notification';
import { cartQueryKeys } from './queries';

const cartUrls = {
  cartArticle: (articleId: string) => `${BASE_BACKEND_URL}/cart/articles/${articleId}` as const,
  order: () => `${BASE_BACKEND_URL}/order` as const,
};

export const usePutArticleInCart = (articleId: string) => {
  const language = useLanguage();
  const queryClient = useQueryClient();
  const pushNotification = useNotification();
  const { t } = useTranslation();

  return useAuthenticatedMutation<PutArticleInCartResponse, PutArticleInCartRequestBody>({
    method: 'PUT',
    url: cartUrls.cartArticle(articleId),
    language,
    options: {
      onSuccess: (response: PutArticleInCartResponse) => {
        pushNotification(
          buildTextAndIconNotification({
            icon: 'cart',
            text: t('cart.addedArticle', { value: articleId }),
          }),
        );
        queryClient.setQueryData(cartQueryKeys.cart(), response);
      },
    },
  });
};

export const usePlaceOrder = (options: Parameters<typeof useAuthenticatedMutation>[0]['options']) => {
  const language = useLanguage();
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<PlaceOrderResponse, PlaceOrderRequestBody>({
    method: 'POST',
    url: cartUrls.order(),
    language,
    options: {
      ...options,
      onSuccess: async (
        response: PlaceOrderResponse,
        _variables: PlaceOrderRequestBody,
        _onMutateResult: unknown,
        _context: MutationFunctionContext,
      ) => {
        await queryClient.resetQueries({
          queryKey: cartQueryKeys.cart(),
          exact: true,
        });
        options?.onSuccess?.(response, _variables, _onMutateResult, _context);
      },
    },
  });
};

export const useRemoveArticleInCart = (
  articleId: string,
  container: Container,
  erpContainerArticleId: string,
  weightKg: number,
) => {
  const language = useLanguage();
  const queryClient = useQueryClient();
  const pushNotification = useNotification();
  const { t } = useTranslation();
  const { mutateAsync: putArticleInCart } = usePutArticleInCart(articleId);

  return useAuthenticatedMutation<PutArticleInCartResponse, null>({
    method: 'DELETE',
    url: cartUrls.cartArticle(articleId),
    language,
    options: {
      onSuccess: (response: PutArticleInCartResponse) => {
        pushNotification(
          buildMaxConfigNotification({
            icon: 'cart',
            text: t('cart.removedArticle', { value: articleId }),
            action: {
              type: 'button',
              text: t('global.reset'),
              onClick: () => {
                putArticleInCart({
                  erpContainerArticleId,
                  container,
                  requestedWeight: weightKg ?? 0,
                });
              },
            },
          }),
        );
        queryClient.setQueryData(cartQueryKeys.cart(), response);
      },
      onError: () => {
        pushNotification(
          buildTextAndIconNotification({
            icon: 'cross',
            text: t('global.notify.errorDescription', { operation: '(deleteArticleInCart)' }),
          }),
        );
      },
    },
  });
};
